'use client';

/**
 * AiChatDrawer.tsx — AI-chat som full-height drawer (høyre side)
 *
 * Skall med samme mønster som NotificationDrawer (posisjon, glide-inn,
 * Escape, klikk-utenfor). Innholdet er offisiell Carbon AI Chat
 * (@carbon/ai-chat) — se AiChatDrawerInner.tsx.
 *
 * Chat-instansen mountes kun mens drawer-en er åpen, så hver åpning
 * starter på Carbon-hjemskjermen.
 */

import { useCallback, useEffect, useRef, useState, type CSSProperties, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent } from 'react';
import dynamic from 'next/dynamic';
import type { AiActivity } from '@/types/ai';
import styles from './ai-chat-drawer.module.css';

/**
 * Varighet på lukkeanimasjonen (ms): blå kant trekkes tilbake, deretter glir
 * chatten ut. Må samsvare med .drawer[data-closing] i CSS (900ms forsinkelse
 * + 200ms glide-ut).
 */
const EXIT_MS = 1100;

/** Standard- og minstebredde (32rem) — Carbon AI Chat sidebar-bredde. */
const DEFAULT_WIDTH = 512;
/** Luft som alltid er igjen til venstre for drawer-en. */
const VIEWPORT_MARGIN = 64;
/** Under denne vindusbredden er drawer-en full bredde (mobil) og kan ikke dras. */
const MIN_RESIZE_VIEWPORT = 640;
const STORAGE_KEY = 'inai-chat-width';
/** Varighet på bredde-animasjonen når knappen trykkes. Må samsvare med [data-slow] i CSS. */
const SLOW_WIDTH_MS = 450;
const FULL_STORAGE_KEY = 'inai-chat-full-width';

// Sidemenyen (side-nav.module.css): rail 3rem / åpen 16rem fra 1056px, ellers
// en overlay (0 når lukket, 16rem når åpen). Speiles av --ai-chat-offset i CSS.
const REM_PX = 16;
const SIDE_NAV_BREAKPOINT = 1056;
const sideNavOffsetPx = (expanded: boolean, viewport: number) =>
  (expanded ? 16 : viewport >= SIDE_NAV_BREAKPOINT ? 3 : 0) * REM_PX;

const maxWidth = () => Math.max(DEFAULT_WIDTH, window.innerWidth - VIEWPORT_MARGIN);
const clampWidth = (w: number) => Math.min(Math.max(w, DEFAULT_WIDTH), maxWidth());

function readStoredWidth(): number {
  try {
    const stored = Number(window.localStorage.getItem(STORAGE_KEY));
    return Number.isFinite(stored) && stored > 0 ? clampWidth(stored) : DEFAULT_WIDTH;
  } catch {
    return DEFAULT_WIDTH;
  }
}

// Carbon AI Chat bygger på web components → kun i nettleseren
const AiChatDrawerInner = dynamic(() => import('./AiChatDrawerInner'), {
  ssr: true,
});

interface AiChatDrawerProps {
  /** Om drawer-en er åpen */
  open: boolean;
  /** Lukk-callback (Escape, Carbon-lukk, klikk utenfor). Må være stabil. */
  onClose: () => void;
  /** Om sidemenyen er åpen — «full bredde» stopper ved menyens kant */
  sideNavExpanded: boolean;
  /** Hva AI-en gjør nå (animasjoner i header og drawer) */
  onActivityChange: (activity: AiActivity) => void;
}

export default function AiChatDrawer({
  open,
  onClose,
  sideNavExpanded,
  onActivityChange,
}: AiChatDrawerProps) {
  const [activity, setActivity] = useState<AiActivity>('idle');
  const handleActivity = useCallback(
    (next: AiActivity) => {
      setActivity(next);
      onActivityChange(next);
    },
    [onActivityChange]
  );
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  // Drawer-en blir stående i DOM mens den glir ut: mounted er true fra åpning
  // til utgangsanimasjonen er ferdig. (Tilstandsjustering under render, som
  // React anbefaler for å utlede tilstand fra en prop.)
  const [prevOpen, setPrevOpen] = useState(open);
  const [mounted, setMounted] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) setMounted(true);
  }
  const closing = mounted && !open;

  // Drawer-bredden (px). Under dra oppdateres CSS-variabelen direkte på
  // elementet (ingen rendering per piksel); state settes når draget slippes.
  const drawerRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);
  const [width, setWidth] = useState(DEFAULT_WIDTH);
  const [canResize, setCanResize] = useState(false);
  const [viewportW, setViewportW] = useState(1024);
  const [fullWidth, setFullWidth] = useState(false);
  // Bredde-overgang (full bredde / sidemeny) slås på først etter oppstart, så
  // den lagrede bredden ikke animeres inn ved åpning
  const [settled, setSettled] = useState(false);
  // Knappen (normal ↔ full) animerer saktere enn sidemenyen (150 ms, som menyen)
  const [slowWidth, setSlowWidth] = useState(false);
  const slowTimerRef = useRef<number | undefined>(undefined);

  // Effektiv bredde i px (speiler CSS: full bredde = 100vw − sidemenyens bredde)
  const effectiveWidth = fullWidth ? viewportW - sideNavOffsetPx(sideNavExpanded, viewportW) : width;

  // Leses etter første render (localStorage finnes ikke på serveren)
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setWidth(readStoredWidth());
    try {
      setFullWidth(window.localStorage.getItem(FULL_STORAGE_KEY) === '1');
    } catch {
      // lagring er valgfritt
    }
    const timer = window.setTimeout(() => setSettled(true), 400);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    const onResize = () => {
      setWidth((w) => clampWidth(w));
      setCanResize(window.innerWidth >= MIN_RESIZE_VIEWPORT);
      setViewportW(document.documentElement.clientWidth);
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const changeFullWidth = useCallback((on: boolean) => {
    setFullWidth(on);
    try {
      if (on) window.localStorage.setItem(FULL_STORAGE_KEY, '1');
      else window.localStorage.removeItem(FULL_STORAGE_KEY);
    } catch {
      // lagring er valgfritt
    }
  }, []);

  const toggleFullWidth = useCallback(() => {
    setSlowWidth(true);
    window.clearTimeout(slowTimerRef.current);
    slowTimerRef.current = window.setTimeout(() => setSlowWidth(false), SLOW_WIDTH_MS + 100);
    changeFullWidth(!fullWidth);
  }, [changeFullWidth, fullWidth]);

  useEffect(() => () => window.clearTimeout(slowTimerRef.current), []);

  // Egen bredde (dra/tastatur) avslutter full bredde
  const commitWidth = useCallback((next: number) => {
    const w = clampWidth(next);
    setWidth(w);
    if (fullWidth) changeFullWidth(false);
    try {
      if (w === DEFAULT_WIDTH) window.localStorage.removeItem(STORAGE_KEY);
      else window.localStorage.setItem(STORAGE_KEY, String(w));
    } catch {
      // lagring er valgfritt
    }
  }, [fullWidth, changeFullWidth]);

  const onHandlePointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    // I full-modus starter draget fra faktisk bredde, så det ikke hopper
    const startWidth = clampWidth(drawerRef.current?.getBoundingClientRect().width ?? width);
    dragRef.current = { startX: e.clientX, startWidth };
    if (fullWidth) {
      setWidth(startWidth);
      changeFullWidth(false);
    }
    drawerRef.current?.setAttribute('data-resizing', '');
  };

  const onHandlePointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    // Drawer-en er forankret til høyre: dra mot venstre = bredere
    const next = clampWidth(drag.startWidth + (drag.startX - e.clientX));
    drawerRef.current?.style.setProperty('--ai-chat-width', `${next}px`);
  };

  const endDrag = (e: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    dragRef.current = null;
    drawerRef.current?.removeAttribute('data-resizing');
    commitWidth(drag.startWidth + (drag.startX - e.clientX));
  };

  const onHandleKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const step = e.shiftKey ? 64 : 16;
    if (e.key === 'ArrowLeft') commitWidth(effectiveWidth + step);
    else if (e.key === 'ArrowRight') commitWidth(effectiveWidth - step);
    else if (e.key === 'Home') commitWidth(DEFAULT_WIDTH);
    else if (e.key === 'End') commitWidth(maxWidth());
    else return;
    e.preventDefault();
  };

  // Chatten lukkes (og streamen avbrytes med den): ingen hengende animasjon
  useEffect(() => {
    if (open) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setActivity('idle');
    onActivityChange('idle');
  }, [open, onActivityChange]);

  useEffect(() => {
    if (!closing) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const timer = window.setTimeout(() => setMounted(false), reduceMotion ? 0 : EXIT_MS);
    return () => window.clearTimeout(timer);
  }, [closing]);

  useEffect(() => {
    if (!open) return;

    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;

    // Escape lukker drawer-en
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // Returner fokus til triggeren (AI-ikonet) ved lukking
      previouslyFocusedRef.current?.focus();
    };
  }, [open, onClose]);

  if (!open && !mounted) return null;

  return (
    <>
      {/* Skjerm-overlay — klikk utenfor lukker drawer-en */}
      <div
        className={styles.overlay}
        data-closing={closing || undefined}
        aria-hidden="true"
        onClick={onClose}
      />

      <div
        ref={drawerRef}
        className={styles.drawer}
        style={{ '--ai-chat-width': `${width}px` } as CSSProperties}
        data-closing={closing || undefined}
        data-full={fullWidth || undefined}
        data-settled={settled || undefined}
        data-slow={slowWidth || undefined}
        data-side-nav={sideNavExpanded ? 'expanded' : 'collapsed'}
        data-activity={activity}
        inert={closing}
        role="dialog"
        aria-modal="false"
        aria-label="AI-assistent"
      >
        {canResize && (
          <div
            className={styles.resizeHandle}
            role="separator"
            aria-orientation="vertical"
            aria-label="Endre bredde på chatten"
            aria-valuemin={DEFAULT_WIDTH}
            aria-valuemax={Math.max(DEFAULT_WIDTH, viewportW - VIEWPORT_MARGIN)}
            aria-valuenow={Math.round(effectiveWidth)}
            tabIndex={0}
            onPointerDown={onHandlePointerDown}
            onPointerMove={onHandlePointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onDoubleClick={() => commitWidth(DEFAULT_WIDTH)}
            onKeyDown={onHandleKeyDown}
          />
        )}
        <span className={styles.activityLine} aria-hidden="true" />
        <AiChatDrawerInner
          onClose={onClose}
          onActivityChange={handleActivity}
          drawerWidth={effectiveWidth}
          fullWidth={fullWidth}
          onToggleFullWidth={canResize ? toggleFullWidth : undefined}
        />
      </div>
    </>
  );
}
